import { isResource, resolveId } from '@medplum/core'
import type {
  Goal,
  Group,
  Organization,
  Patient,
  Reference,
} from '@medplum/fhirtypes'
import { codeFromCodeableConcept } from '#src/codeableConcept.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import type { OutcomeStatus } from '#src/models/care-plans/index.ts'
import { isOfType } from '#src/utils.ts'

export class Outcome extends ExtendedResource<Goal> {
  static readonly RESOURCE_TYPE = 'Goal'

  static isInstance(value: unknown): value is Goal {
    return isResource(value) && isOfType(value, 'Goal')
  }

  get title(): string {
    return this.resource.description?.text ?? ''
  }

  set title(title: string) {
    this.resource.description = { text: title }
  }

  get status(): OutcomeStatus {
    return this.resource.lifecycleStatus as OutcomeStatus
  }

  set status(status: OutcomeStatus) {
    this.resource.lifecycleStatus = status
  }

  static build(params: {
    description: string
    subject: Goal['subject']
    note?: string
  }): Outcome {
    return new Outcome({
      lifecycleStatus: 'proposed',
      subject: params.subject,
      description: { text: params.description },
      note: params.note ? [{ text: params.note }] : undefined,
    })
  }

  get achievementStatusCode(): string | undefined {
    return codeFromCodeableConcept(this.resource.achievementStatus)
  }

  get startDate(): string | undefined {
    return this.resource.startDate
  }

  get statusDate(): string | undefined {
    return this.resource.statusDate
  }

  get categoryCodes(): string[] | undefined {
    return this.resource.category?.map((c) => codeFromCodeableConcept(c) ?? '')
  }

  get priorityCode(): string | undefined {
    return codeFromCodeableConcept(this.resource.priority)
  }

  get noteSummary(): string | undefined {
    return this.resource.note?.map((n) => n.text).join(', ')
  }

  get subject(): Reference<Patient | Group | Organization> {
    return this.resource.subject
  }

  protected getExportFields() {
    return {
      id: this.id,
      title: this.title,
      status: this.status,
      patientId: resolveId(this.subject),
      achievementStatus: this.achievementStatusCode,
      startDate: this.startDate,
      statusDate: this.statusDate,
      category: this.categoryCodes,
      priority: this.priorityCode,
      note: this.noteSummary,
    }
  }
}
